import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Menu,
  Grid,
  Snackbar,
  Autocomplete,
  LinearProgress,
  Collapse,
  Divider,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Paid as PaidIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Undo as UndoIcon,
  AccountBalance as AccountBalanceIcon,
  BarChart as BarChartIcon,
  PeopleAlt as PeopleAltIcon,
  TableRows as TableRowsIcon,
} from '@mui/icons-material';
import {
  useEggSales,
  useEggSalesStats,
  useCreateEggSaleBatch,
  useUpdateEggSale,
  useDeleteEggSale,
  useMarkSalePaid,
  useBulkMarkSalesPaid,
} from '../hooks/useEggSales';
import {
  useDepositStats,
  useCreateDeposit,
  useBulkCompleteDeposits,
  useCorrectDeposits,
  useReverseDeposits,
  useSaleDeposits,
  useDeleteDeposit,
} from '../hooks/useDeposits';
import { useCustomers } from '../hooks/useCustomers';
import { useBirdProcurements } from '../hooks/useBirds';
import { useActivePrices, useCustomerPrice } from '../hooks/usePricing';
import { EggSale, SaleDeposit, Customer, BirdProcurement, Price } from '../types';
import BrokenEggSalesPage from './BrokenEggSales';

type ComparisonMode = 'day' | 'week' | 'month';

const fmtDate = (d: Date) => d.toISOString().split('T')[0];

const DEPOSIT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending Deposit' },
  { value: 'partial', label: 'Partial Deposit' },
  { value: 'completed', label: 'Fully Deposited' },
];

const getComparisonRanges = (mode: ComparisonMode) => {
  const today = new Date();

  if (mode === 'day') {
    const prev = new Date(today);
    prev.setDate(prev.getDate() - 7);
    return {
      current: { date_from: fmtDate(today), date_to: fmtDate(today), label: 'Today' },
      previous: { date_from: fmtDate(prev), date_to: fmtDate(prev), label: '7 days ago' },
    };
  }

  if (mode === 'week') {
    const dayOfWeek = today.getDay();
    const daysToMon = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const thisMonday = new Date(today);
    thisMonday.setDate(today.getDate() - daysToMon);
    const lastSunday = new Date(thisMonday);
    lastSunday.setDate(thisMonday.getDate() - 1);
    const lastMonday = new Date(lastSunday);
    lastMonday.setDate(lastSunday.getDate() - 6);
    return {
      current: { date_from: fmtDate(thisMonday), date_to: fmtDate(today), label: 'This Week' },
      previous: {
        date_from: fmtDate(lastMonday),
        date_to: fmtDate(lastSunday),
        label: 'Last Week',
      },
    };
  }

  // month
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const sameDay = Math.min(today.getDate(), prevMonthEnd.getDate());
  const prevSameDay = new Date(today.getFullYear(), today.getMonth() - 1, sameDay);
  return {
    current: { date_from: fmtDate(thisMonthStart), date_to: fmtDate(today), label: 'This Month' },
    previous: {
      date_from: fmtDate(prevMonthStart),
      date_to: fmtDate(prevSameDay),
      label: 'Last Month',
    },
  };
};

const SalesComparisonPanel: React.FC = () => {
  const [mode, setMode] = useState<ComparisonMode>('day');
  const [expanded, setExpanded] = useState(false);
  const ranges = getComparisonRanges(mode);

  const { data: currentStats, isFetching: currentFetching } = useEggSalesStats({
    date_from: ranges.current.date_from,
    date_to: ranges.current.date_to,
  });

  const { data: prevStats, isFetching: prevFetching } = useEggSalesStats({
    date_from: ranges.previous.date_from,
    date_to: ranges.previous.date_to,
  });

  const metrics = [
    {
      label: 'Revenue',
      current: Number(currentStats?.total_revenue || 0),
      prev: Number(prevStats?.total_revenue || 0),
      format: (v: number) => `GH₵ ${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      color: '#ff9800',
      higherIsBetter: true,
    },
    {
      label: 'Crates Sold',
      current: Number(currentStats?.total_quantity_sold || 0),
      prev: Number(prevStats?.total_quantity_sold || 0),
      format: (v: number) => v.toLocaleString(),
      color: '#2196f3',
      higherIsBetter: true,
    },
    {
      label: 'Amount Paid',
      current: Number(currentStats?.total_paid || 0),
      prev: Number(prevStats?.total_paid || 0),
      format: (v: number) => `GH₵ ${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      color: '#66bb6a',
      higherIsBetter: true,
    },
    {
      label: 'Outstanding',
      current: Number(currentStats?.total_outstanding || 0),
      prev: Number(prevStats?.total_outstanding || 0),
      format: (v: number) => `GH₵ ${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      color: '#f44336',
      higherIsBetter: false,
    },
  ];

  const getDelta = (current: number, prev: number) => {
    if (prev === 0) return current > 0 ? 100 : 0;
    return ((current - prev) / prev) * 100;
  };

  return (
    <Paper sx={{ border: '1px solid rgba(255, 152, 0, 0.3)', borderRadius: 0 }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.05)' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <TrendingUpIcon sx={{ color: '#ff9800' }} />
          <Typography variant="h6" fontWeight={600} sx={{ color: '#ffffff' }}>
            Sales Comparison
          </Typography>
        </Box>
        <IconButton
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
            color: '#ff9800',
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 2.5, pt: 1 }}>
          {/* Mode selector */}
          <Box display="flex" gap={1} mb={2}>
            {(['day', 'week', 'month'] as ComparisonMode[]).map((m) => (
              <Chip
                key={m}
                label={m === 'day' ? 'Day' : m === 'week' ? 'Week' : 'Month'}
                onClick={(e) => {
                  e.stopPropagation();
                  setMode(m);
                }}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: mode === m ? '#ff9800' : 'transparent',
                  color: mode === m ? '#000' : 'rgba(255,255,255,0.6)',
                  borderColor: mode === m ? '#ff9800' : 'rgba(255,255,255,0.2)',
                  border: '1px solid',
                  fontWeight: mode === m ? 700 : 400,
                  '&:hover': {
                    backgroundColor: mode === m ? '#ffa726' : 'rgba(255,152,0,0.08)',
                  },
                }}
              />
            ))}
          </Box>

          {/* Date range labels */}
          {(() => {
            const currentRange =
              ranges.current.date_from === ranges.current.date_to
                ? ranges.current.date_from
                : `${ranges.current.date_from} → ${ranges.current.date_to}`;
            const prevRange =
              ranges.previous.date_from === ranges.previous.date_to
                ? ranges.previous.date_from
                : `${ranges.previous.date_from} → ${ranges.previous.date_to}`;
            return (
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mb: 2 }}
              >
                <Box component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>
                  {ranges.current.label}
                </Box>{' '}
                ({currentRange}) vs{' '}
                <Box component="span" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
                  {ranges.previous.label}
                </Box>{' '}
                ({prevRange})
              </Typography>
            );
          })()}

          {(currentFetching || prevFetching) && (
            <LinearProgress
              sx={{
                mb: 2,
                height: 2,
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
                },
              }}
            />
          )}

          <Grid
            container
            spacing={2}
            sx={{ opacity: currentFetching || prevFetching ? 0.6 : 1, transition: 'opacity 0.3s' }}
          >
            {metrics.map((metric) => {
              const delta = getDelta(metric.current, metric.prev);
              const isUp = delta > 0;
              const isGood = metric.higherIsBetter ? isUp : !isUp && delta !== 0;
              const isBad = metric.higherIsBetter ? !isUp && delta !== 0 : isUp;
              return (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={metric.label}>
                  <Paper
                    sx={{
                      p: 2,
                      background: 'linear-gradient(135deg, #1e1e1e 0%, #141414 100%)',
                      border: `1px solid ${metric.color}22`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.45)',
                        display: 'block',
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {metric.label}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: metric.color, mb: 0.25 }}
                    >
                      {metric.format(metric.current)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.35)', mb: 1, fontSize: '0.78rem' }}
                    >
                      vs {metric.format(metric.prev)}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${isUp ? '+' : ''}${delta.toFixed(1)}%`}
                      sx={{
                        height: 22,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        backgroundColor: isGood
                          ? 'rgba(76,175,80,0.15)'
                          : isBad
                            ? 'rgba(244,67,54,0.15)'
                            : 'rgba(255,255,255,0.08)',
                        color: isGood ? '#66bb6a' : isBad ? '#f44336' : 'rgba(255,255,255,0.4)',
                        border: `1px solid ${
                          isGood
                            ? 'rgba(76,175,80,0.3)'
                            : isBad
                              ? 'rgba(244,67,54,0.3)'
                              : 'rgba(255,255,255,0.15)'
                        }`,
                      }}
                    />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
};

// ── Customer Grouped View ────────────────────────────────────────────────────

const parsePriceCents = (val: string): number =>
  parseFloat((val ?? '').replace(/[^0-9.]/g, '')) || 0;

const fmtGHS = (n: number) =>
  `GH₵ ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface CustomerSalesGroup {
  customer_id: number;
  customer_name: string;
  customer_phone: string | null;
  sales: EggSale[];
  total_value: number;
  total_paid: number;
  total_to_deposit: number;
  total_crates: number;
}

const CustomerGroupedView: React.FC<{
  filters: {
    start_date: string;
    end_date: string;
    customer_ids: number[];
    exclude_customer_ids: number[];
    procurement_id: number | null;
  };
  onRecordDeposit: (sale: EggSale) => void;
  onMarkCustomerDeposited: (group: CustomerSalesGroup) => Promise<void>;
  onRevertDeposit: (sale: EggSale) => void;
  revertingIds: Set<number>;
}> = ({ filters, onRecordDeposit, onMarkCustomerDeposited, onRevertDeposit, revertingIds }) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [depositingIds, setDepositingIds] = useState<Set<number>>(new Set());

  const { data, isFetching } = useEggSales({
    per_page: 500,
    date_from: filters.start_date || undefined,
    date_to: filters.end_date || undefined,
    customer_ids: filters.customer_ids.length ? filters.customer_ids : undefined,
    not_customer_ids: filters.exclude_customer_ids.length
      ? filters.exclude_customer_ids
      : undefined,
    procurement_id: filters.procurement_id || undefined,
  });

  const groups = useMemo((): CustomerSalesGroup[] => {
    const sales = data?.data ?? [];
    const map = new Map<number, CustomerSalesGroup>();
    for (const sale of sales) {
      const cid = sale.customer_id;
      if (!map.has(cid)) {
        map.set(cid, {
          customer_id: cid,
          customer_name: sale.customer?.name ?? `Customer #${cid}`,
          customer_phone: (sale.customer as any)?.phone_number ?? null,
          sales: [],
          total_value: 0,
          total_paid: 0,
          total_to_deposit: 0,
          total_crates: 0,
        });
      }
      const g = map.get(cid)!;
      const paid = parsePriceCents(sale.amount_paid);
      const deposited = parsePriceCents((sale as any).amount_deposited ?? 'GH₵ 0.00');
      g.sales.push(sale);
      g.total_value += parsePriceCents(sale.total_price);
      g.total_paid += paid;
      g.total_to_deposit += Math.max(0, paid - deposited);
      g.total_crates += sale.crate_quantity;
    }
    return Array.from(map.values()).sort((a, b) => b.total_to_deposit - a.total_to_deposit);
  }, [data]);

  const toggle = (id: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress sx={{ color: '#ff9800' }} />
      </Box>
    );
  }

  if (!groups.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <Typography color="text.secondary">No sales found</Typography>
      </Box>
    );
  }

  const totalToDeposit = groups.reduce((s, g) => s + g.total_to_deposit, 0);
  const customersWithPending = groups.filter((g) => g.total_to_deposit > 0.01).length;

  return (
    <Box sx={{ p: 2, overflowY: 'auto', flex: 1, minHeight: 0 }}>
      {/* Summary bar */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid rgba(244,67,54,0.2)',
          display: 'flex',
          gap: 4,
          alignItems: 'center',
          background: 'linear-gradient(135deg,#161616,#1e1e1e)',
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.4)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'block',
            }}
          >
            Pending to Deposit
          </Typography>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: totalToDeposit > 0 ? '#ffa726' : '#4caf50' }}
          >
            {fmtGHS(totalToDeposit)}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.4)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'block',
            }}
          >
            Customers with Pending Deposits
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#ff9800' }}>
            {customersWithPending} / {groups.length}
          </Typography>
        </Box>
      </Paper>

      {/* Customer cards */}
      <Box display="flex" flexDirection="column" gap={1.5}>
        {groups.map((group) => {
          const isExpanded = expandedIds.has(group.customer_id);
          const hasPending = group.total_to_deposit > 0.01;
          return (
            <Paper
              key={group.customer_id}
              sx={{
                border: `1px solid ${hasPending ? 'rgba(255,167,38,0.25)' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {/* Card header — click to expand */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                }}
                onClick={() => toggle(group.customer_id)}
              >
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>
                      {group.customer_name}
                    </Typography>
                    {hasPending ? (
                      <Chip
                        label={`To Deposit ${fmtGHS(group.total_to_deposit)}`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,167,38,0.12)',
                          color: '#ffa726',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          height: 22,
                        }}
                      />
                    ) : (
                      <Chip
                        label="Fully Deposited"
                        size="small"
                        sx={{
                          bgcolor: 'rgba(76,175,80,0.12)',
                          color: '#4caf50',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          height: 22,
                        }}
                      />
                    )}
                  </Box>
                  {group.customer_phone && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                      {group.customer_phone}
                    </Typography>
                  )}
                </Box>

                {/* Totals summary */}
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 3, mr: 1 }}>
                  <Box textAlign="right">
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}
                    >
                      {group.sales.length} sales · {group.total_crates} crates
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#ff9800' }}>
                      {fmtGHS(group.total_value)}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}
                    >
                      Paid
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#4caf50' }}>
                      {fmtGHS(group.total_paid)}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}
                    >
                      To Deposit
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: hasPending ? '#ffa726' : 'rgba(255,255,255,0.3)' }}
                    >
                      {hasPending ? fmtGHS(group.total_to_deposit) : '—'}
                    </Typography>
                  </Box>
                </Box>

                {hasPending && (
                  <Tooltip title="Mark all this customer's pending amounts as deposited" arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled={depositingIds.has(group.customer_id)}
                        onClick={async (e) => {
                          e.stopPropagation();
                          setDepositingIds((prev) => new Set(prev).add(group.customer_id));
                          try {
                            await onMarkCustomerDeposited(group);
                          } finally {
                            setDepositingIds((prev) => {
                              const next = new Set(prev);
                              next.delete(group.customer_id);
                              return next;
                            });
                          }
                        }}
                        sx={{
                          color: '#4caf50',
                          '&:hover': { bgcolor: 'rgba(76,175,80,0.1)' },
                          mr: 0.5,
                        }}
                      >
                        {depositingIds.has(group.customer_id) ? (
                          <CircularProgress size={16} sx={{ color: '#4caf50' }} />
                        ) : (
                          <AccountBalanceIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <IconButton
                  size="small"
                  sx={{ color: '#ff9800', ml: 0.5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(group.customer_id);
                  }}
                >
                  <ExpandMoreIcon
                    sx={{
                      transform: isExpanded ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  />
                </IconButton>
              </Box>

              {/* Expanded sale rows */}
              <Collapse in={isExpanded}>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                {/* Column headers */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '90px 1fr 60px 90px 100px 100px 100px 76px 44px',
                    px: 2,
                    py: 0.75,
                    bgcolor: 'rgba(255,152,0,0.05)',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  {[
                    'Date',
                    'Batch',
                    'Crates',
                    'Unit Price',
                    'Total',
                    'Paid',
                    'To Deposit',
                    'Status',
                    '',
                  ].map((h) => (
                    <Typography
                      key={h}
                      variant="caption"
                      sx={{
                        color: 'rgba(255,152,0,0.6)',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        fontSize: '0.6rem',
                      }}
                    >
                      {h}
                    </Typography>
                  ))}
                </Box>
                {group.sales.map((sale, idx) => {
                  const paid = parsePriceCents(sale.amount_paid);
                  const deposited = parsePriceCents((sale as any).amount_deposited ?? 'GH₵ 0.00');
                  const toDeposit = Math.max(0, paid - deposited);
                  const isFullyDeposited = toDeposit <= 0.01;
                  const depositStatus = isFullyDeposited
                    ? 'deposited'
                    : deposited > 0
                      ? 'partial'
                      : 'pending';
                  return (
                    <Box
                      key={sale.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '90px 1fr 60px 90px 100px 100px 100px 76px 44px',
                        px: 2,
                        py: 1,
                        alignItems: 'center',
                        borderBottom:
                          idx < group.sales.length - 1
                            ? '1px solid rgba(255,255,255,0.04)'
                            : 'none',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}
                      >
                        {new Date(sale.sales_date).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                        })}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}
                      >
                        {(sale as any).birds?.identifier ?? `#${sale.procurement_id}`}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#ff9800', fontWeight: 600, fontSize: '0.8rem' }}
                      >
                        {sale.crate_quantity}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem' }}
                      >
                        {fmtGHS(parsePriceCents(sale.unit_price))}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}
                      >
                        {sale.total_price}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#4caf50', fontWeight: 600, fontSize: '0.8rem' }}
                      >
                        {sale.amount_paid}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: toDeposit > 0.01 ? '#ffa726' : 'rgba(255,255,255,0.3)',
                          fontWeight: toDeposit > 0.01 ? 700 : 400,
                          fontSize: '0.8rem',
                        }}
                      >
                        {toDeposit > 0.01 ? fmtGHS(toDeposit) : '—'}
                      </Typography>
                      <Chip
                        label={depositStatus}
                        size="small"
                        color={
                          depositStatus === 'deposited'
                            ? 'success'
                            : depositStatus === 'partial'
                              ? 'warning'
                              : 'error'
                        }
                        sx={{
                          textTransform: 'capitalize',
                          fontWeight: 600,
                          fontSize: '0.68rem',
                          height: 20,
                        }}
                      />
                      {!isFullyDeposited && paid > 0 ? (
                        <Tooltip title="Record deposit">
                          <IconButton
                            size="small"
                            onClick={() => onRecordDeposit(sale)}
                            sx={{ color: '#4caf50', p: 0.5 }}
                          >
                            <MoneyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      ) : isFullyDeposited ? (
                        <Tooltip title="Revert deposit">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onRevertDeposit(sale)}
                              disabled={revertingIds.has(sale.id)}
                              sx={{ color: 'rgba(255,100,100,0.7)', p: 0.5 }}
                            >
                              <UndoIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Box />
                      )}
                    </Box>
                  );
                })}
              </Collapse>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

interface SaleLine {
  id: string;
  procurement_id: number;
  crate_quantity: string;
  price_id: number;
  unit_price: string;
  amount_paid: string;
  selectedPrice: Price | null;
}

const createEmptyLine = (): SaleLine => ({
  id: crypto.randomUUID(),
  procurement_id: -1,
  crate_quantity: '',
  price_id: -1,
  unit_price: '',
  amount_paid: '',
  selectedPrice: null,
});

const EggSalesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openDepositDialog, setOpenDepositDialog] = useState(false);
  const [openBulkDepositDialog, setOpenBulkDepositDialog] = useState(false);
  const [openBulkMarkPaidDialog, setOpenBulkMarkPaidDialog] = useState(false);
  const [openReverseDepositDialog, setOpenReverseDepositDialog] = useState(false);
  const [reverseDepositDate, setReverseDepositDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [openManageDepositsDialog, setOpenManageDepositsDialog] = useState(false);
  const [manageDepositsSale, setManageDepositsSale] = useState<EggSale | null>(null);
  const [confirmDeleteDepositId, setConfirmDeleteDepositId] = useState<number | null>(null);
  const [editingSale, setEditingSale] = useState<EggSale | null>(null);
  const [paymentSale, setPaymentSale] = useState<EggSale | null>(null);
  const [depositSale, setDepositSale] = useState<EggSale | null>(null);
  const [revertingIds, setRevertingIds] = useState<Set<number>>(new Set());
  const [revertConfirmSale, setRevertConfirmSale] = useState<EggSale | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSale, setSelectedSale] = useState<EggSale | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'customer'>('customer');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [filters, setFilters] = useState({
    start_date: searchParams.get('start_date') || '',
    end_date: searchParams.get('end_date') || '',
    customer_ids: searchParams.get('customer_ids')
      ? searchParams.get('customer_ids')?.split(',')?.map(Number) || []
      : [],
    exclude_customer_ids: searchParams.get('exclude_customer_ids')
      ? searchParams.get('exclude_customer_ids')?.split(',')?.map(Number) || []
      : [],
    procurement_id: searchParams.get('procurement_id')
      ? Number(searchParams.get('procurement_id'))
      : null,
    deposit_status: searchParams.get('deposit_status') || '',
  });
  const [formData, setFormData] = useState({
    customer_id: -1,
    sales_date: new Date().toISOString().split('T')[0],
  });
  const [saleLines, setSaleLines] = useState<SaleLine[]>([createEmptyLine()]);
  const [paymentData, setPaymentData] = useState({
    amount_paid: '',
    payment_date: new Date().toISOString().split('T')[0],
  });
  const [depositData, setDepositData] = useState({
    amount_deposited: '',
    deposit_date: new Date().toISOString().split('T')[0],
    deposit_reference: '',
    notes: '',
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // React Query hooks
  const {
    data: salesData,
    isFetching: salesFetching,
    error,
    refetch,
  } = useEggSales({
    page: paginationModel.page + 1,
    per_page: paginationModel.pageSize,
    date_from: filters.start_date || undefined,
    date_to: filters.end_date || undefined,
    customer_ids: filters.customer_ids.length ? filters.customer_ids : undefined,
    not_customer_ids: filters.exclude_customer_ids.length
      ? filters.exclude_customer_ids
      : undefined,
    procurement_id: filters.procurement_id || undefined,
    deposit_status: filters.deposit_status,
  });
  const { data: stats, isFetching: isFetchingStats } = useEggSalesStats({
    date_from: filters.start_date || undefined,
    date_to: filters.end_date || undefined,
    customer_ids: filters.customer_ids.length ? filters.customer_ids : undefined,
    not_customer_ids: filters.exclude_customer_ids.length
      ? filters.exclude_customer_ids
      : undefined,
    procurement_id: filters.procurement_id || undefined,
  });
  const { data: customersData, isFetching: customersFetching } = useCustomers();
  const { data: procurementsData, isFetching: procurementsFetching } = useBirdProcurements();
  const {
    data: activePrices,
    isLoading: pricesLoading,
    isFetching: pricesFetching,
  } = useActivePrices();

  // Fetch customer-specific pricing
  const crateQty = saleLines[0]?.crate_quantity ? parseInt(saleLines[0].crate_quantity) : 0;
  const customerId = formData.customer_id && formData.customer_id > 0 ? formData.customer_id : null;
  const { data: customerPriceData } = useCustomerPrice(customerId, crateQty);

  // Filter only laying birds
  const layingProcurements = procurementsData?.data?.filter((procurement: BirdProcurement) =>
    procurement.birds?.some((bird) => bird.laying_start_date !== null)
  );

  const createMutation = useCreateEggSaleBatch();
  const bulkMarkPaidMutation = useBulkMarkSalesPaid();
  const updateMutation = useUpdateEggSale();
  const deleteMutation = useDeleteEggSale();
  const markPaidMutation = useMarkSalePaid();
  const createDepositMutation = useCreateDeposit();
  const bulkCompleteDepositsMutation = useBulkCompleteDeposits();
  const correctDepositsMutation = useCorrectDeposits();
  const reverseDepositsMutation = useReverseDeposits();
  const deleteDepositMutation = useDeleteDeposit();
  const { data: saleDepositsData, isLoading: saleDepositsLoading } = useSaleDeposits(
    manageDepositsSale?.id ?? 0
  );

  // Fetch deposit stats
  const { data: depositStats, isFetching: depositStatsFetching } = useDepositStats({
    date_from: filters.start_date || undefined,
    date_to: filters.end_date || undefined,
    customer_ids: filters.customer_ids.length ? filters.customer_ids : undefined,
    not_customer_ids: filters.exclude_customer_ids.length
      ? filters.exclude_customer_ids
      : undefined,
    procurement_id: filters.procurement_id || undefined,
  });

  // Auto-select customer-specific price if available — apply to all lines
  useEffect(() => {
    if (customerPriceData?.price && openDialog && !editingSale) {
      const price = customerPriceData.price;
      const extractNumericValue = (value: string | number): string => {
        if (typeof value === 'number') return value.toString();
        return value.replace(/[^0-9.]/g, '');
      };
      const unitPrice = extractNumericValue(price.price_per_crate);
      setSaleLines((prev) =>
        prev.map((line) => ({
          ...line,
          price_id: price.id,
          unit_price: unitPrice,
          selectedPrice: price,
        }))
      );
    }
  }, [customerPriceData, openDialog, editingSale]);

  const parsePrice = (priceString: string): number => {
    return parseFloat(priceString.replace(/[^0-9.-]+/g, ''));
  };

  const showSnackbar = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Backfill selectedPrice when activePrices loads after the edit dialog is already open
  useEffect(() => {
    if (!activePrices || !editingSale) return;
    setSaleLines((prev) =>
      prev.map((l) =>
        l.selectedPrice === null && l.price_id > 0
          ? { ...l, selectedPrice: activePrices.find((p: Price) => p.id === l.price_id) ?? null }
          : l
      )
    );
  }, [activePrices, editingSale]);

  // Sync filters with URL query params
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.start_date) params.set('start_date', filters.start_date);
    if (filters.end_date) params.set('end_date', filters.end_date);
    if (filters.customer_ids.length) params.set('customer_ids', filters.customer_ids.join(','));
    if (filters.exclude_customer_ids.length)
      params.set('exclude_customer_ids', filters.exclude_customer_ids.join(','));
    if (filters.procurement_id) params.set('procurement_id', filters.procurement_id.toString());
    if (filters.deposit_status) params.set('deposit_status', filters.deposit_status);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const handleFilterChange = (field: keyof typeof filters, value: any) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  };

  const handleClearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      customer_ids: [],
      exclude_customer_ids: [],
      procurement_id: null,
      deposit_status: '',
    });
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  };

  const hasActiveFilters =
    filters.start_date ||
    filters.end_date ||
    filters.customer_ids.length > 0 ||
    filters.exclude_customer_ids.length > 0 ||
    filters.procurement_id ||
    filters.deposit_status;

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Failed to Load Egg Sales Data
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {error instanceof Error
                ? error.message
                : 'An error occurred while fetching egg sales data. Please try again.'}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => refetch()} sx={{ color: '#fff' }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  const handleOpenDialog = (sale?: EggSale) => {
    if (sale) {
      setEditingSale(sale);
      setFormData({
        customer_id: sale.customer_id,
        sales_date: sale.sales_date.split('T')[0],
      });
      const priceId = sale.price_id ? parseInt(sale.price_id) : -1;
      setSaleLines([
        {
          id: crypto.randomUUID(),
          procurement_id: sale.procurement_id,
          crate_quantity: sale.crate_quantity.toString(),
          unit_price: parsePrice(sale.unit_price).toString(),
          amount_paid: parsePrice(sale.amount_paid).toString(),
          price_id: priceId,
          selectedPrice: activePrices?.find((p: Price) => p.id === priceId) ?? null,
        },
      ]);
    } else {
      setEditingSale(null);
      setFormData({
        customer_id: -1,
        sales_date: new Date().toISOString().split('T')[0],
      });
      setSaleLines([createEmptyLine()]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSale(null);
  };

  const handleOpenPaymentDialog = (sale: EggSale) => {
    setPaymentSale(sale);
    setPaymentData({
      amount_paid: parsePrice(sale.amount_left).toString(),
      payment_date: new Date().toISOString().split('T')[0],
    });
    setOpenPaymentDialog(true);
    handleCloseMenu();
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setPaymentSale(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePaymentInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentData({
      ...paymentData,
      [e.target.name]: e.target.value,
    });
  };

  const updateLine = (id: string, patch: Partial<SaleLine>) => {
    setSaleLines((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, ...patch };
        const qty = parseFloat(updated.crate_quantity);
        const price = parseFloat(updated.unit_price);
        if (!isNaN(qty) && !isNaN(price) && qty > 0 && price > 0) {
          updated.amount_paid = (qty * price).toFixed(2);
        }
        return updated;
      })
    );
  };

  const handleSubmit = async () => {
    if (formData.customer_id === -1) {
      showSnackbar('Please select a customer', 'warning');
      return;
    }
    if (!formData.sales_date) {
      showSnackbar('Please select a sale date', 'warning');
      return;
    }

    for (const line of saleLines) {
      if (line.procurement_id === -1) {
        showSnackbar('Please select a bird/procurement for every line', 'warning');
        return;
      }
      if (!line.crate_quantity || parseInt(line.crate_quantity) <= 0) {
        showSnackbar('Please enter a valid quantity for every line', 'warning');
        return;
      }
      if (!line.unit_price || parseFloat(line.unit_price) <= 0) {
        showSnackbar('Please enter a valid unit price for every line', 'warning');
        return;
      }
    }

    try {
      if (editingSale) {
        const line = saleLines[0];
        const amountPaid = parseFloat(line.amount_paid || '0');
        const total = parseInt(line.crate_quantity) * parseFloat(line.unit_price);
        await updateMutation.mutateAsync({
          id: editingSale.id,
          data: {
            procurement_id: line.procurement_id,
            customer_id: formData.customer_id,
            sales_date: formData.sales_date,
            crate_quantity: parseInt(line.crate_quantity),
            unit_price: parseFloat(line.unit_price),
            is_paid: amountPaid >= total,
            amount_paid: amountPaid,
            price_id: line.price_id > 0 ? line.price_id : undefined,
          },
        });
        showSnackbar('Sale updated successfully', 'success');
      } else {
        await createMutation.mutateAsync({
          customer_id: formData.customer_id,
          sales_date: formData.sales_date,
          items: saleLines.map((line) => ({
            procurement_id: line.procurement_id,
            crate_quantity: parseInt(line.crate_quantity),
            unit_price: parseFloat(line.unit_price),
            amount_paid: parseFloat(line.amount_paid || '0') || undefined,
            price_id: line.price_id > 0 ? line.price_id : undefined,
          })),
        });
        showSnackbar(
          saleLines.length > 1
            ? `${saleLines.length} sales recorded successfully`
            : 'Sale recorded successfully',
          'success'
        );
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving egg sale:', error);
      showSnackbar('Failed to save sale. Please try again.', 'error');
    }
  };

  const handleMarkPaid = async () => {
    if (!paymentSale) return;

    // Validation
    if (!paymentData.amount_paid || parseFloat(paymentData.amount_paid) <= 0) {
      showSnackbar('Please enter a valid payment amount greater than 0', 'warning');
      return;
    }

    if (!paymentData.payment_date) {
      showSnackbar('Please select a payment date', 'warning');
      return;
    }

    const amountPaid = parseFloat(paymentData.amount_paid);
    const outstandingBalance = parsePrice(paymentSale.amount_left);

    if (amountPaid > outstandingBalance) {
      showSnackbar(
        `Payment amount (GH₵ ${amountPaid.toFixed(2)}) cannot exceed outstanding balance (${paymentSale.amount_left})`,
        'error'
      );
      return;
    }

    try {
      await markPaidMutation.mutateAsync({
        id: paymentSale.id,
        data: {
          amount_paid: amountPaid,
          payment_date: paymentData.payment_date,
        },
      });
      showSnackbar('Payment recorded successfully', 'success');
      handleClosePaymentDialog();
    } catch (error) {
      console.error('Error marking sale as paid:', error);
      showSnackbar('Failed to record payment. Please try again.', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this sale?')) {
      try {
        await deleteMutation.mutateAsync(id);
        showSnackbar('Sale deleted successfully', 'success');
        handleCloseMenu();
      } catch (error) {
        console.error('Error deleting sale:', error);
        showSnackbar('Failed to delete sale. Please try again.', 'error');
      }
    }
  };

  const handleOpenDepositDialog = (sale: EggSale) => {
    setDepositSale(sale);
    const amountPaid = parsePrice(sale.amount_paid);
    const amountDeposited = parsePrice(sale.amount_deposited || 'GH₵ 0.00');
    const pendingDeposit = amountPaid - amountDeposited;
    setDepositData({
      amount_deposited: pendingDeposit > 0 ? pendingDeposit.toString() : '',
      deposit_date: new Date().toISOString().split('T')[0],
      deposit_reference: '',
      notes: '',
    });
    setOpenDepositDialog(true);
    handleCloseMenu();
  };

  const handleCloseDepositDialog = () => {
    setOpenDepositDialog(false);
    setDepositSale(null);
  };

  const handleRevertDeposit = (sale: EggSale) => {
    setRevertConfirmSale(sale);
  };

  const confirmRevertDeposit = async () => {
    if (!revertConfirmSale) return;
    const sale = revertConfirmSale;
    setRevertConfirmSale(null);
    setRevertingIds((prev) => new Set(prev).add(sale.id));
    try {
      const result = await reverseDepositsMutation.mutateAsync({ egg_sale_id: sale.id });
      showSnackbar(result.message || 'Deposit reverted', 'success');
    } catch {
      showSnackbar('Failed to revert deposit', 'error');
    } finally {
      setRevertingIds((prev) => {
        const next = new Set(prev);
        next.delete(sale.id);
        return next;
      });
    }
  };

  const handleDeposit = async () => {
    if (!depositSale) return;

    // Validation
    if (!depositData.amount_deposited || parseFloat(depositData.amount_deposited) <= 0) {
      showSnackbar('Please enter a valid deposit amount greater than 0', 'warning');
      return;
    }

    if (!depositData.deposit_date) {
      showSnackbar('Please select a deposit date', 'warning');
      return;
    }

    const amountDeposit = parseFloat(depositData.amount_deposited);
    const amountPaid = parsePrice(depositSale.amount_paid);
    const alreadyDeposited = parsePrice(depositSale.amount_deposited || 'GH₵ 0.00');
    const pendingDeposit = amountPaid - alreadyDeposited;

    if (amountDeposit > pendingDeposit) {
      showSnackbar(
        `Deposit amount (GH₵ ${amountDeposit.toFixed(2)}) cannot exceed pending deposit (GH₵ ${pendingDeposit.toFixed(2)})`,
        'error'
      );
      return;
    }

    try {
      await createDepositMutation.mutateAsync({
        egg_sale_id: depositSale.id,
        amount_deposited: amountDeposit,
        deposit_date: depositData.deposit_date,
        deposit_reference: depositData.deposit_reference || undefined,
        notes: depositData.notes || undefined,
      });
      showSnackbar('Deposit recorded successfully', 'success');
      handleCloseDepositDialog();
    } catch (error: any) {
      console.error('Error recording deposit:', error);
      showSnackbar(
        error?.response?.data?.message || 'Failed to record deposit. Please try again.',
        'error'
      );
    }
  };

  const handleBulkCompleteDeposits = async () => {
    try {
      const appliedFilters: any = {};
      if (filters.customer_ids && filters.customer_ids.length > 0)
        appliedFilters.customer_ids = filters.customer_ids.join(',');
      if (filters.procurement_id) appliedFilters.procurement_id = filters.procurement_id;
      if (filters.start_date) appliedFilters.date_from = filters.start_date;
      if (filters.end_date) appliedFilters.date_to = filters.end_date;
      if (filters.exclude_customer_ids && filters.exclude_customer_ids.length > 0)
        appliedFilters.not_customer_ids = filters.exclude_customer_ids.join(',');

      const result = await bulkCompleteDepositsMutation.mutateAsync(appliedFilters);
      showSnackbar(result.message || 'Deposits marked as completed successfully', 'success');
      setOpenBulkDepositDialog(false);
    } catch (error: any) {
      console.error('Error bulk completing deposits:', error);
      showSnackbar(
        error?.response?.data?.message || 'Failed to complete deposits. Please try again.',
        'error'
      );
    }
  };

  const handleBulkMarkPaid = async () => {
    try {
      const appliedFilters: any = {};
      if (filters.customer_ids?.length)
        appliedFilters.customer_ids = filters.customer_ids.join(',');
      if (filters.exclude_customer_ids?.length)
        appliedFilters.not_customer_ids = filters.exclude_customer_ids.join(',');
      if (filters.procurement_id) appliedFilters.procurement_id = filters.procurement_id;
      if (filters.start_date) appliedFilters.date_from = filters.start_date;
      if (filters.end_date) appliedFilters.date_to = filters.end_date;

      const result = await bulkMarkPaidMutation.mutateAsync(appliedFilters);
      showSnackbar(result.message || 'Sales marked as paid', 'success');
      setOpenBulkMarkPaidDialog(false);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || 'Failed to mark sales as paid', 'error');
    }
  };

  const handleReverseDeposits = async () => {
    try {
      const filters: any = { date_from: reverseDepositDate, date_to: reverseDepositDate };
      if (filters.customer_ids && filters.customer_ids.length > 0)
        filters.customer_ids = filters.customer_ids.join(',');
      const result = await reverseDepositsMutation.mutateAsync(filters);
      showSnackbar(result.message || 'Deposits reversed successfully', 'success');
      setOpenReverseDepositDialog(false);
    } catch (error: any) {
      showSnackbar(
        error?.response?.data?.message || 'Failed to reverse deposits. Please try again.',
        'error'
      );
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, sale: EggSale) => {
    setAnchorEl(event.currentTarget);
    setSelectedSale(sale);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedSale(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'partial':
        return 'warning';
      case 'pending':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'sales_date',
      headerName: 'Sale Date',
      flex: 1,
      minWidth: 110,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2">
            {new Date(params.value).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'customer',
      headerName: 'Customer',
      flex: 1.2,
      minWidth: 140,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2" fontWeight={600}>
            {params.row.customer?.name || 'Unknown'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'birds',
      headerName: 'Bird',
      flex: 1,
      minWidth: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2" fontWeight={500} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {params.row.birds?.identifier || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'crate_quantity',
      headerName: 'Quantity',
      flex: 0.8,
      minWidth: 100,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2" sx={{ color: '#ff9800' }} fontWeight={600}>
            {params.value?.toLocaleString()} crates
          </Typography>
        </Box>
      ),
    },
    {
      field: 'unit_price',
      headerName: 'Unit Price',
      flex: 0.8,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'total_price',
      headerName: 'Total Amount',
      flex: 1,
      minWidth: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2" fontWeight={700}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'amount_paid',
      headerName: 'Paid',
      flex: 0.9,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2" sx={{ color: '#66bb6a' }} fontWeight={600}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'amount_left',
      headerName: 'Balance',
      flex: 0.9,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography
            variant="body2"
            sx={{ color: parsePrice(params.value) > 0 ? '#f44336' : 'rgba(255, 255, 255, 0.7)' }}
            fontWeight={parsePrice(params.value) > 0 ? 600 : 400}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'amount_deposited',
      headerName: 'Deposited',
      flex: 0.9,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const deposited = params.value || 'GH₵ 0.00';
        const amountPaid = parsePrice(params.row.amount_paid);
        const amountDeposited = parsePrice(deposited);
        const isFullyDeposited = amountDeposited >= amountPaid && amountPaid > 0;
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography
              variant="body2"
              sx={{
                color: isFullyDeposited
                  ? '#4caf50'
                  : amountDeposited > 0
                    ? '#ffa726'
                    : 'rgba(255, 255, 255, 0.5)',
              }}
              fontWeight={amountDeposited > 0 ? 600 : 400}
            >
              {deposited}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'deposits_max_deposit_date',
      headerName: 'Deposit Date',
      flex: 1,
      minWidth: 110,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          {params.value ? (
            <Typography variant="body2">
              {new Date(params.value).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.35)' }}>
              —
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'is_paid',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value
          ? 'paid'
          : parsePrice(params.row.amount_paid) > 0
            ? 'partial'
            : 'pending';
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Chip
              label={status}
              color={getStatusColor(status)}
              size="small"
              sx={{ textTransform: 'capitalize', fontWeight: 600 }}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          onClick={(e) => handleOpenMenu(e, params.row)}
          sx={{ color: '#ff9800' }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const sales = salesData?.data || [];

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Subtle loading indicator */}
      {(salesFetching ||
        isFetchingStats ||
        customersFetching ||
        procurementsFetching ||
        pricesFetching ||
        depositStatsFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 2 }}>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
            Egg Sales
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Track egg sales, customer payments, and bank deposits
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg,#ff9800,#ffa726)',
            color: '#fff',
            px: 2,
            '&:hover': { background: 'linear-gradient(135deg,#f57c00,#ff9800)' },
          }}
        >
          Record Sale
        </Button>
      </Box>

      {/* ── Tabs ── */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 1.5,
          minHeight: 36,
          '& .MuiTabs-indicator': { backgroundColor: '#ff9800' },
          '& .MuiTab-root': {
            color: 'rgba(255,255,255,0.45)',
            minHeight: 36,
            py: 0.5,
            textTransform: 'none',
            fontWeight: 600,
          },
          '& .Mui-selected': { color: '#ff9800' },
        }}
      >
        <Tab label="Normal Sales" />
        <Tab label="Broken Egg Sales" />
      </Tabs>

      {activeTab === 0 && (
        <>
          {/* ── Unified stats strip ── */}
          <Paper
            sx={{
              mb: 1.5,
              px: 0,
              py: 0,
              background: 'linear-gradient(135deg,#161616,#1e1e1e)',
              border: '1px solid rgba(255,152,0,0.12)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
              {[
                {
                  label: 'Revenue',
                  value: `GH₵ ${Number(stats?.total_revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                  sub: `${typeof stats?.payment_rate === 'number' ? stats.payment_rate.toFixed(0) : '0'}% rate`,
                  color: '#ff9800',
                  dividerColor: 'rgba(255,152,0,0.08)',
                },
                {
                  label: 'Paid',
                  value: `GH₵ ${Number(stats?.total_paid || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                  sub: undefined,
                  color: '#66bb6a',
                  dividerColor: 'rgba(255,152,0,0.08)',
                },
                {
                  label: 'Outstanding',
                  value: `GH₵ ${Number(stats?.total_outstanding || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                  sub: `${stats?.pending_sales_count || 0} pending`,
                  color: '#f44336',
                  dividerColor: 'rgba(255,152,0,0.08)',
                },
                {
                  label: 'Crates Sold',
                  value: Number(stats?.total_quantity_sold || 0).toLocaleString(),
                  sub: undefined,
                  color: '#2196f3',
                  dividerColor: 'rgba(255,255,255,0.06)',
                },
                {
                  label: 'Deposited',
                  value: `GH₵ ${Number(depositStats?.total_deposited || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                  sub: `${depositStats?.deposit_rate?.toFixed(0) || '0'}% rate`,
                  color: '#4caf50',
                  dividerColor: 'rgba(76,175,80,0.08)',
                },
                {
                  label: 'Pending Deposit',
                  value: `GH₵ ${Number(depositStats?.pending_deposit || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                  sub: undefined,
                  color: '#ffa726',
                  dividerColor: 'rgba(76,175,80,0.08)',
                },
                {
                  label: 'Deposit Txns',
                  value: Number(depositStats?.deposit_count || 0).toLocaleString(),
                  sub: `${depositStats?.fully_deposited_sales || 0} full`,
                  color: '#26c6da',
                  dividerColor: undefined,
                },
              ].map((s, i, arr) => (
                <React.Fragment key={s.label}>
                  <Box
                    sx={{
                      flex: 1,
                      py: 1.25,
                      px: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255,255,255,0.4)',
                          fontSize: '0.6rem',
                          display: 'block',
                        }}
                      >
                        {s.label.toUpperCase()}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography
                          fontWeight={700}
                          sx={{ color: s.color, lineHeight: 1.1, fontSize: '0.92rem' }}
                        >
                          {s.value}
                        </Typography>
                        {s.sub && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.62rem' }}
                          >
                            {s.sub}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  {i < arr.length - 1 && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ borderColor: s.dividerColor || 'rgba(255,255,255,0.06)' }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Paper>

          {/* ── Table Paper ── */}
          <Paper
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid rgba(255,152,0,0.15)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {/* Toolbar row */}
            <Box
              sx={{
                px: 2,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                background: 'linear-gradient(135deg,#161616,#1e1e1e)',
                borderBottom: '1px solid rgba(255,152,0,0.1)',
              }}
            >
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: 'rgba(255,255,255,0.7)', flexShrink: 0 }}
              >
                Records
              </Typography>
              <Chip
                label={salesData?.meta?.total || 0}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,152,0,0.1)',
                  color: '#ff9800',
                  fontWeight: 700,
                  height: 18,
                  fontSize: '0.7rem',
                }}
              />
              <Box flex={1} />

              {/* Action icon buttons */}
              {depositStats && depositStats.pending_deposit < 0 && (
                <Tooltip title="Fix Deposit Data">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      try {
                        const res = await correctDepositsMutation.mutateAsync();
                        showSnackbar(
                          res?.message || 'Deposit data corrected successfully',
                          'success'
                        );
                      } catch {
                        showSnackbar('Failed to correct deposit data', 'error');
                      }
                    }}
                    disabled={correctDepositsMutation.isPending}
                    sx={{ color: '#f44336', '&:hover': { bgcolor: 'rgba(244,67,54,0.1)' } }}
                  >
                    {correctDepositsMutation.isPending ? (
                      <CircularProgress size={16} sx={{ color: '#f44336' }} />
                    ) : (
                      <MoneyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Mark All Paid">
                <IconButton
                  size="small"
                  onClick={() => setOpenBulkMarkPaidDialog(true)}
                  sx={{ color: '#1976d2', '&:hover': { bgcolor: 'rgba(25,118,210,0.1)' } }}
                >
                  <PaidIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {depositStats && depositStats.pending_deposit > 0 && (
                <Tooltip title="Complete Deposits">
                  <IconButton
                    size="small"
                    onClick={() => setOpenBulkDepositDialog(true)}
                    sx={{ color: '#4caf50', '&:hover': { bgcolor: 'rgba(76,175,80,0.1)' } }}
                  >
                    <PaymentIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Revert Deposits">
                <IconButton
                  size="small"
                  onClick={() => setOpenReverseDepositDialog(true)}
                  sx={{ color: '#ef5350', '&:hover': { bgcolor: 'rgba(239,83,80,0.1)' } }}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Sales Analytics">
                <IconButton
                  size="small"
                  onClick={() => setAnalyticsOpen(true)}
                  sx={{ color: '#ffa726', '&:hover': { bgcolor: 'rgba(255,167,38,0.1)' } }}
                >
                  <BarChartIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 0.5 }}
              />

              <Tooltip title="Table View">
                <IconButton
                  size="small"
                  onClick={() => setViewMode('table')}
                  sx={{
                    color: viewMode === 'table' ? '#ff9800' : 'rgba(255,255,255,0.35)',
                    bgcolor: viewMode === 'table' ? 'rgba(255,152,0,0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(255,152,0,0.1)' },
                  }}
                >
                  <TableRowsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="By Customer">
                <IconButton
                  size="small"
                  onClick={() => setViewMode('customer')}
                  sx={{
                    color: viewMode === 'customer' ? '#ff9800' : 'rgba(255,255,255,0.35)',
                    bgcolor: viewMode === 'customer' ? 'rgba(255,152,0,0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(255,152,0,0.1)' },
                  }}
                >
                  <PeopleAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 0.5 }}
              />

              <Tooltip title={filtersOpen ? 'Hide Filters' : 'Show Filters'}>
                <IconButton
                  size="small"
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  sx={{
                    color: hasActiveFilters ? '#ff9800' : 'rgba(255,255,255,0.4)',
                    '&:hover': { bgcolor: 'rgba(255,152,0,0.1)' },
                  }}
                >
                  <FilterListIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {hasActiveFilters && (
                <Tooltip title="Clear Filters">
                  <IconButton
                    size="small"
                    onClick={handleClearFilters}
                    sx={{
                      color: 'rgba(255,255,255,0.4)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Collapsible filter inputs */}
            <Collapse in={filtersOpen}>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid rgba(255,152,0,0.08)',
                  background: 'rgba(255,152,0,0.02)',
                }}
              >
                <Grid container spacing={1.5} alignItems="center">
                  <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={filters.start_date}
                      onChange={(e) => handleFilterChange('start_date', e.target.value)}
                      fullWidth
                      size="small"
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                      label="End Date"
                      type="date"
                      value={filters.end_date}
                      onChange={(e) => handleFilterChange('end_date', e.target.value)}
                      fullWidth
                      size="small"
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2.5 }}>
                    <Autocomplete
                      options={customersData?.data || []}
                      getOptionLabel={(option) => option.name}
                      value={
                        customersData?.data?.filter((c: Customer) =>
                          filters.customer_ids.includes(c.id)
                        ) || []
                      }
                      onChange={(_, newValue) =>
                        handleFilterChange(
                          'customer_ids',
                          newValue.map((v) => v.id)
                        )
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Customer" size="small" />
                      )}
                      size="small"
                      multiple
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2.5 }}>
                    <Autocomplete
                      options={
                        customersData?.data?.filter(
                          (c: Customer) => !filters.customer_ids.includes(c.id)
                        ) || []
                      }
                      getOptionLabel={(option) => option.name}
                      value={
                        customersData?.data?.filter((c: Customer) =>
                          filters.exclude_customer_ids.includes(c.id)
                        ) || []
                      }
                      onChange={(_, newValue) =>
                        handleFilterChange(
                          'exclude_customer_ids',
                          newValue.map((v) => v.id)
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Exclude Customers"
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: 'rgba(244,67,54,0.4)' },
                              '&:hover fieldset': { borderColor: 'rgba(244,67,54,0.6)' },
                              '&.Mui-focused fieldset': { borderColor: '#f44336' },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(244,67,54,0.8)' },
                          }}
                        />
                      )}
                      size="small"
                      multiple
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 2 }}>
                    <Autocomplete
                      options={layingProcurements || []}
                      getOptionLabel={(option) => option.identifier || 'N/A'}
                      value={
                        layingProcurements?.find(
                          (p: BirdProcurement) => p.id === filters.procurement_id
                        ) || null
                      }
                      onChange={(_, newValue) =>
                        handleFilterChange('procurement_id', newValue?.id || null)
                      }
                      renderInput={(params) => <TextField {...params} label="Batch" size="small" />}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 1.5 }}>
                    <Autocomplete
                      options={DEPOSIT_STATUS_OPTIONS}
                      getOptionLabel={(o) => o.label}
                      value={
                        DEPOSIT_STATUS_OPTIONS.find((o) => o.value === filters.deposit_status) ??
                        null
                      }
                      onChange={(_, newValue) =>
                        handleFilterChange('deposit_status', newValue?.value ?? '')
                      }
                      size="small"
                      renderInput={(params) => (
                        <TextField {...params} label="Deposit Status" size="small" />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>

            {/* Main content: table or by-customer */}
            {viewMode === 'table' ? (
              <DataGrid
                rows={sales}
                columns={columns}
                rowCount={salesData?.meta?.total || 0}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                disableRowSelectionOnClick
                rowHeight={44}
                loading={salesFetching}
                sx={{
                  flex: 1,
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    color: '#fff',
                    fontSize: '0.82rem',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    bgcolor: 'rgba(255,152,0,0.07)',
                    borderBottom: '1px solid rgba(255,152,0,0.15)',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    letterSpacing: '0.7px',
                    color: 'rgba(255,152,0,0.7)',
                    textTransform: 'uppercase',
                  },
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(255,152,0,0.05)' },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '1px solid rgba(255,152,0,0.12)',
                    bgcolor: 'rgba(255,152,0,0.03)',
                    minHeight: 40,
                  },
                  '& .MuiTablePagination-root': {
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '0.78rem',
                  },
                  '& .MuiTablePagination-selectIcon': { color: 'rgba(255,255,255,0.4)' },
                }}
              />
            ) : (
              <CustomerGroupedView
                filters={filters}
                onRecordDeposit={handleOpenDepositDialog}
                onRevertDeposit={handleRevertDeposit}
                revertingIds={revertingIds}
                onMarkCustomerDeposited={async (group) => {
                  const result = await bulkCompleteDepositsMutation.mutateAsync({
                    customer_ids: group.customer_id.toString(),
                  });
                  showSnackbar(
                    result.message || `Deposits marked for ${group.customer_name}`,
                    'success'
                  );
                }}
              />
            )}
          </Paper>

          {/* Analytics Dialog */}
          <Dialog
            open={analyticsOpen}
            onClose={() => setAnalyticsOpen(false)}
            maxWidth="md"
            fullWidth
            slotProps={{
              paper: { sx: { border: '1px solid rgba(255,152,0,0.3)', borderRadius: 2 } },
            }}
          >
            <DialogTitle sx={{ pb: 1, borderBottom: '1px solid rgba(255,152,0,0.2)' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUpIcon sx={{ color: '#ff9800' }} />
                <Typography variant="h6" fontWeight={700} sx={{ color: '#ff9800' }}>
                  Sales Analytics
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <SalesComparisonPanel />
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid rgba(255,152,0,0.1)', px: 2, py: 1 }}>
              <Button
                onClick={() => setAnalyticsOpen(false)}
                size="small"
                sx={{ color: 'rgba(255,255,255,0.5)' }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Actions Menu */}
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
            {selectedSale && !selectedSale.is_paid && (
              <MenuItem onClick={() => handleOpenPaymentDialog(selectedSale)}>
                <PaymentIcon fontSize="small" sx={{ mr: 1 }} />
                Record Payment
              </MenuItem>
            )}
            {selectedSale && selectedSale.is_paid && !selectedSale.is_fully_deposited && (
              <MenuItem onClick={() => handleOpenDepositDialog(selectedSale)}>
                <MoneyIcon fontSize="small" sx={{ mr: 1, color: '#4caf50' }} />
                Record Deposit
              </MenuItem>
            )}
            {selectedSale &&
              selectedSale.is_paid &&
              parseFloat(selectedSale.amount_deposited?.replace(/[^0-9.]/g, '') || '0') > 0 && (
                <MenuItem
                  onClick={() => {
                    setManageDepositsSale(selectedSale);
                    setOpenManageDepositsDialog(true);
                    handleCloseMenu();
                  }}
                >
                  <AccountBalanceIcon fontSize="small" sx={{ mr: 1, color: '#2196f3' }} />
                  Manage Deposits
                </MenuItem>
              )}
            {selectedSale && (
              <MenuItem onClick={() => handleOpenDialog(selectedSale)}>
                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                Edit
              </MenuItem>
            )}
            {selectedSale && (
              <MenuItem onClick={() => handleDelete(selectedSale.id)} sx={{ color: 'error.main' }}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            )}
          </Menu>

          {/* Create/Edit Dialog */}
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            maxWidth="sm"
            fullWidth
            slotProps={{
              paper: { sx: { border: '1px solid rgba(255,152,0,0.3)', borderRadius: 2 } },
            }}
          >
            <DialogTitle sx={{ pb: 2, borderBottom: '1px solid rgba(255, 152, 0, 0.3)' }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#ff9800' }}>
                {editingSale ? 'Edit Sale' : 'Record New Sale'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} mt={0.5}>
                {editingSale ? 'Update sale details' : 'Enter the egg sale information'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2.5} mt={2}>
                {/* Shared fields */}
                <Autocomplete
                  options={customersData?.data || []}
                  getOptionLabel={(c: Customer) => c.name}
                  value={
                    customersData?.data?.find((c: Customer) => c.id === formData.customer_id) ??
                    null
                  }
                  onChange={(_, newValue) =>
                    setFormData((prev) => ({ ...prev, customer_id: newValue?.id ?? -1 }))
                  }
                  renderInput={(params) => <TextField {...params} label="Customer" required />}
                />
                <TextField
                  label="Sale Date"
                  name="sales_date"
                  type="date"
                  value={formData.sales_date}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                />

                {/* Per-batch lines */}
                {saleLines.map((line, index) => {
                  const lineTotal =
                    line.unit_price && line.crate_quantity
                      ? parseFloat(line.unit_price) * parseInt(line.crate_quantity)
                      : 0;
                  return (
                    <Paper
                      key={line.id}
                      variant="outlined"
                      sx={{ p: 2, borderColor: 'rgba(255,152,0,0.3)', borderRadius: 2 }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1.5}
                      >
                        <Typography variant="subtitle2" sx={{ color: '#ff9800' }}>
                          Batch {index + 1}
                        </Typography>
                        {saleLines.length > 1 && !editingSale && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setSaleLines((prev) => prev.filter((l) => l.id !== line.id))
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Autocomplete
                          options={layingProcurements || []}
                          getOptionLabel={(p: BirdProcurement) => p.identifier}
                          value={
                            layingProcurements?.find(
                              (p: BirdProcurement) => p.id === line.procurement_id
                            ) ?? null
                          }
                          onChange={(_, newValue) =>
                            updateLine(line.id, { procurement_id: newValue?.id ?? -1 })
                          }
                          renderInput={(params) => <TextField {...params} label="Bird" required />}
                        />
                        <TextField
                          label="Quantity (crates)"
                          type="number"
                          value={line.crate_quantity}
                          onChange={(e) => updateLine(line.id, { crate_quantity: e.target.value })}
                          fullWidth
                          required
                          helperText="1 crate = 30 eggs"
                        />
                        <Autocomplete
                          options={activePrices || []}
                          getOptionLabel={(option) => {
                            let label = option.price_per_crate;
                            if (option.name) label += ` - ${option.name}`;
                            if (option.category) label += ` (${option.category.name})`;
                            return label;
                          }}
                          value={line.selectedPrice}
                          onChange={(_, newValue) => {
                            const extractNumeric = (v: string | number) =>
                              typeof v === 'number' ? v.toString() : v.replace(/[^0-9.]/g, '');
                            updateLine(line.id, {
                              selectedPrice: newValue,
                              price_id: newValue?.id ?? -1,
                              unit_price: newValue ? extractNumeric(newValue.price_per_crate) : '',
                            });
                          }}
                          loading={pricesLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Unit Price (GH₵)"
                              required
                              helperText={
                                customerPriceData?.is_override
                                  ? 'Customer-specific pricing applied'
                                  : 'Select price per crate'
                              }
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <Box>
                                <Typography variant="body1" fontWeight={600}>
                                  {option.price_per_crate}
                                </Typography>
                                {option.name && (
                                  <Typography variant="body2" color="text.secondary">
                                    {option.name}
                                  </Typography>
                                )}
                                {option.category && (
                                  <Typography variant="caption" color="text.secondary">
                                    {option.category.name}
                                  </Typography>
                                )}
                                {!!option.min_crates && !!option.max_crates && (
                                  <Typography variant="caption" color="primary">
                                    {' '}
                                    • For {option.min_crates}–{option.max_crates} crates
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                        />
                        {!!lineTotal && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            px={1}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Subtotal
                            </Typography>
                            <Typography variant="body1" fontWeight={700} sx={{ color: '#ff9800' }}>
                              GH₵ {lineTotal.toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                        {!!lineTotal && (
                          <TextField
                            label="Amount Paid (GH₵)"
                            type="number"
                            value={line.amount_paid}
                            onChange={(e) => updateLine(line.id, { amount_paid: e.target.value })}
                            fullWidth
                            helperText={`Maximum: GH₵ ${lineTotal.toFixed(2)}`}
                            slotProps={{ htmlInput: { step: '0.01' } }}
                          />
                        )}
                      </Box>
                    </Paper>
                  );
                })}

                {/* Grand total + add line */}
                {!editingSale && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setSaleLines((prev) => [...prev, createEmptyLine()])}
                      sx={{ borderColor: 'rgba(255,152,0,0.5)', color: '#ff9800' }}
                    >
                      + Add Another Batch
                    </Button>
                    {saleLines.length > 1 && (
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#ff9800' }}>
                        Grand Total: GH₵{' '}
                        {saleLines
                          .reduce((sum, l) => {
                            const qty = parseInt(l.crate_quantity || '0');
                            const price = parseFloat(l.unit_price || '0');
                            return sum + qty * price;
                          }, 0)
                          .toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                px: 3,
                pb: 3,
                borderTop: '1px solid rgba(255, 152, 0, 0.3)',
                backgroundColor: 'rgba(255, 152, 0, 0.05)',
              }}
            >
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{ color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.3)' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={createMutation.isPending || updateMutation.isPending}
                sx={{
                  background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
                  color: '#ffffff',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
                  },
                }}
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <CircularProgress size={24} />
                ) : editingSale ? (
                  'Update'
                ) : (
                  'Save'
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Payment Dialog */}
          <Dialog
            open={openPaymentDialog}
            onClose={handleClosePaymentDialog}
            maxWidth="sm"
            fullWidth
            slotProps={{
              paper: { sx: { border: '1px solid rgba(76,175,80,0.3)', borderRadius: 2 } },
            }}
          >
            <DialogTitle sx={{ pb: 2, borderBottom: '1px solid rgba(76, 175, 80, 0.3)' }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#66bb6a' }}>
                Record Payment
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} mt={0.5}>
                Enter payment details for this sale
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
                {paymentSale && (
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary">
                      Outstanding Balance
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="error.main">
                      {paymentSale.amount_left}
                    </Typography>
                  </Paper>
                )}
                <TextField
                  label="Amount Paid (GH₵)"
                  name="amount_paid"
                  type="number"
                  value={paymentData.amount_paid}
                  onChange={handlePaymentInputChange}
                  fullWidth
                  required
                  slotProps={{ htmlInput: { step: '0.01' } }}
                />
                <TextField
                  label="Payment Date"
                  name="payment_date"
                  type="date"
                  value={paymentData.payment_date}
                  onChange={handlePaymentInputChange}
                  fullWidth
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                px: 3,
                pb: 3,
                borderTop: '1px solid rgba(76, 175, 80, 0.3)',
                backgroundColor: 'rgba(76, 175, 80, 0.05)',
              }}
            >
              <Button
                onClick={handleClosePaymentDialog}
                variant="outlined"
                sx={{ color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.3)' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkPaid}
                variant="contained"
                disabled={markPaidMutation.isPending}
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                  color: '#ffffff',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                  },
                }}
              >
                {markPaidMutation.isPending ? <CircularProgress size={24} /> : 'Record Payment'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Deposit Dialog */}
          <Dialog
            open={openDepositDialog}
            onClose={handleCloseDepositDialog}
            maxWidth="sm"
            fullWidth
            slotProps={{
              paper: { sx: { border: '1px solid rgba(76,175,80,0.3)', borderRadius: 2 } },
            }}
          >
            <DialogTitle sx={{ pb: 2, borderBottom: '1px solid rgba(76, 175, 80, 0.3)' }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#4caf50' }}>
                Record Bank Deposit
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} mt={0.5}>
                {depositSale &&
                  `Sale #${depositSale.id} - ${depositSale.customer?.name || 'Customer'}`}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2.5} mt={2}>
                {depositSale && (
                  <Paper
                    sx={{
                      p: 2,
                      background: 'rgba(76, 175, 80, 0.1)',
                      border: '1px solid rgba(76, 175, 80, 0.3)',
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Amount Paid:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {depositSale.amount_paid}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Already Deposited:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {depositSale.amount_deposited || 'GH₵ 0.00'}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Pending Deposit:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#ffa726' }}>
                        GH₵{' '}
                        {(
                          parsePrice(depositSale.amount_paid) -
                          parsePrice(depositSale.amount_deposited || 'GH₵ 0.00')
                        ).toFixed(2)}
                      </Typography>
                    </Box>
                  </Paper>
                )}

                <TextField
                  label="Deposit Amount (GH₵)"
                  type="number"
                  name="amount_deposited"
                  value={depositData.amount_deposited}
                  onChange={(e) =>
                    setDepositData({ ...depositData, amount_deposited: e.target.value })
                  }
                  fullWidth
                  required
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                  helperText="Amount being deposited to the bank"
                />

                <TextField
                  label="Deposit Date"
                  type="date"
                  name="deposit_date"
                  value={depositData.deposit_date}
                  onChange={(e) => setDepositData({ ...depositData, deposit_date: e.target.value })}
                  fullWidth
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                />

                <TextField
                  label="Bank Reference/Transaction ID"
                  name="deposit_reference"
                  value={depositData.deposit_reference}
                  onChange={(e) =>
                    setDepositData({ ...depositData, deposit_reference: e.target.value })
                  }
                  fullWidth
                  helperText="Optional: Bank transaction reference number"
                />

                <TextField
                  label="Notes"
                  name="notes"
                  value={depositData.notes}
                  onChange={(e) => setDepositData({ ...depositData, notes: e.target.value })}
                  multiline
                  rows={3}
                  fullWidth
                  helperText="Optional: Additional notes about this deposit"
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(76, 175, 80, 0.3)' }}>
              <Button onClick={handleCloseDepositDialog} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Cancel
              </Button>
              <Button
                onClick={handleDeposit}
                variant="contained"
                disabled={createDepositMutation.isPending}
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                  color: '#ffffff',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                  },
                }}
              >
                {createDepositMutation.isPending ? (
                  <CircularProgress size={24} />
                ) : (
                  'Record Deposit'
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Manage Deposits Dialog */}
          <Dialog
            open={openManageDepositsDialog}
            onClose={() => {
              setOpenManageDepositsDialog(false);
              setManageDepositsSale(null);
              setConfirmDeleteDepositId(null);
            }}
            maxWidth="sm"
            fullWidth
            slotProps={{
              paper: { sx: { border: '1px solid rgba(33,150,243,0.3)', borderRadius: 2 } },
            }}
          >
            <DialogTitle sx={{ pb: 2, borderBottom: '1px solid rgba(33,150,243,0.3)' }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#2196f3' }}>
                Deposit History
              </Typography>
              {manageDepositsSale && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }} mt={0.5}>
                  Sale #{manageDepositsSale.id} · {manageDepositsSale.customer?.name || 'Customer'}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              <Box mt={2} display="flex" flexDirection="column" gap={1.5}>
                {manageDepositsSale && (
                  <Paper
                    sx={{
                      p: 1.5,
                      background: 'rgba(33,150,243,0.08)',
                      border: '1px solid rgba(33,150,243,0.2)',
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        Amount Paid
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {manageDepositsSale.amount_paid}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Total Deposited
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#4caf50' }}>
                        {manageDepositsSale.amount_deposited || 'GH₵ 0.00'}
                      </Typography>
                    </Box>
                  </Paper>
                )}
                {saleDepositsLoading ? (
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                  </Box>
                ) : !saleDepositsData?.deposits?.length ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                    No deposit records found
                  </Typography>
                ) : (
                  saleDepositsData.deposits.map((deposit: SaleDeposit) => (
                    <Paper
                      key={deposit.id}
                      sx={{
                        p: 1.5,
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#4caf50' }}>
                            {deposit.amount_deposited}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {deposit.deposit_date
                              ? new Date(deposit.deposit_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : '—'}
                            {deposit.deposit_reference && ` · Ref: ${deposit.deposit_reference}`}
                          </Typography>
                          {deposit.notes && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {deposit.notes}
                            </Typography>
                          )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5} ml={1}>
                          {confirmDeleteDepositId === deposit.id ? (
                            <>
                              <Button
                                size="small"
                                onClick={() => setConfirmDeleteDepositId(null)}
                                sx={{ color: 'rgba(255,255,255,0.6)', minWidth: 0 }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="error"
                                onClick={async () => {
                                  try {
                                    await deleteDepositMutation.mutateAsync(deposit.id);
                                    setConfirmDeleteDepositId(null);
                                    showSnackbar('Deposit reversed successfully', 'success');
                                  } catch {
                                    showSnackbar('Failed to reverse deposit', 'error');
                                  }
                                }}
                                disabled={deleteDepositMutation.isPending}
                                sx={{ minWidth: 0 }}
                              >
                                {deleteDepositMutation.isPending ? (
                                  <CircularProgress size={14} sx={{ color: '#fff' }} />
                                ) : (
                                  'Reverse'
                                )}
                              </Button>
                            </>
                          ) : (
                            <IconButton
                              size="small"
                              title="Reverse this deposit"
                              onClick={() => setConfirmDeleteDepositId(deposit.id)}
                              sx={{ color: 'error.main' }}
                            >
                              <UndoIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid rgba(33,150,243,0.2)', pt: 1.5 }}>
              <Button
                onClick={() => {
                  setOpenManageDepositsDialog(false);
                  setManageDepositsSale(null);
                  setConfirmDeleteDepositId(null);
                }}
                sx={{ color: 'rgba(255,255,255,0.7)' }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Bulk Complete Deposits Confirmation Dialog */}
          <Dialog
            open={openBulkDepositDialog}
            onClose={() => setOpenBulkDepositDialog(false)}
            maxWidth="sm"
            fullWidth
            slotProps={{
              paper: { sx: { border: '1px solid rgba(76,175,80,0.3)', borderRadius: 2 } },
            }}
          >
            <DialogTitle sx={{ pb: 2, borderBottom: '1px solid rgba(76, 175, 80, 0.3)' }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#4caf50' }}>
                Confirm Bulk Deposit Completion
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This will mark all pending deposits as completed for sales matching your current
                  filters.
                </Alert>
                <Typography variant="body1" gutterBottom>
                  <strong>Current Filters Applied:</strong>
                </Typography>
                <Box sx={{ pl: 2, mt: 1 }}>
                  {filters.start_date && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      • Start Date: {new Date(filters.start_date).toLocaleDateString()}
                    </Typography>
                  )}
                  {filters.end_date && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      • End Date: {new Date(filters.end_date).toLocaleDateString()}
                    </Typography>
                  )}
                  {filters.customer_ids && filters.customer_ids.length > 0 && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      • Customer:{' '}
                      {customersData?.data
                        ?.filter((c: Customer) => filters.customer_ids.includes(c.id))
                        .map((c: Customer) => c.name)
                        .join(', ')}
                    </Typography>
                  )}
                  {filters.procurement_id && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      • Batch:{' '}
                      {
                        layingProcurements?.find(
                          (p: BirdProcurement) => p.id === filters.procurement_id
                        )?.identifier
                      }
                    </Typography>
                  )}
                  {!filters.start_date &&
                    !filters.end_date &&
                    (!filters.customer_ids || filters.customer_ids.length === 0) &&
                    !filters.procurement_id && (
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        • No filters applied - will process all pending deposits
                      </Typography>
                    )}
                </Box>
                {depositStats && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 2 }}>
                    <Typography variant="body1" fontWeight={600} sx={{ color: '#4caf50' }}>
                      Pending Deposit Amount: GH₵{' '}
                      {depositStats.pending_deposit?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                )}
                <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
                  Are you sure you want to mark all pending deposits as completed?
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(76, 175, 80, 0.3)' }}>
              <Button
                onClick={() => setOpenBulkDepositDialog(false)}
                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkCompleteDeposits}
                variant="contained"
                disabled={bulkCompleteDepositsMutation.isPending}
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                  color: '#ffffff',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                  },
                }}
              >
                {bulkCompleteDepositsMutation.isPending ? (
                  <CircularProgress size={24} />
                ) : (
                  'Confirm & Complete All'
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Bulk Mark Paid Confirmation Dialog */}
          <Dialog
            open={openBulkMarkPaidDialog}
            onClose={() => setOpenBulkMarkPaidDialog(false)}
            maxWidth="sm"
            fullWidth
            slotProps={{
              paper: { sx: { border: '1px solid rgba(25,118,210,0.3)', borderRadius: 2 } },
            }}
          >
            <DialogTitle sx={{ pb: 2, borderBottom: '1px solid rgba(25,118,210,0.3)' }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#1976d2' }}>
                Mark All Sales Paid
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Box>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  This will mark all unpaid sales as fully paid using their total price.
                </Typography>
                {(filters.customer_ids?.length > 0 ||
                  filters.start_date ||
                  filters.end_date ||
                  filters.procurement_id) && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(25,118,210,0.08)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                      Active filters will be applied — only matching unpaid sales will be updated.
                    </Typography>
                  </Box>
                )}
                <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.7)' }}>
                  Are you sure you want to continue?
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(25,118,210,0.3)' }}>
              <Button
                onClick={() => setOpenBulkMarkPaidDialog(false)}
                sx={{ color: 'rgba(255,255,255,0.7)' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkMarkPaid}
                variant="contained"
                disabled={bulkMarkPaidMutation.isPending}
                sx={{
                  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                  color: '#ffffff',
                  '&:hover': { background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)' },
                }}
              >
                {bulkMarkPaidMutation.isPending ? (
                  <CircularProgress size={24} />
                ) : (
                  'Confirm & Mark All Paid'
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Revert Deposits Confirmation Dialog */}
          <Dialog
            open={openReverseDepositDialog}
            onClose={() => setOpenReverseDepositDialog(false)}
            maxWidth="sm"
            fullWidth
            slotProps={{
              paper: { sx: { border: '1px solid rgba(239,83,80,0.3)', borderRadius: 2 } },
            }}
          >
            <DialogTitle sx={{ pb: 2, borderBottom: '1px solid rgba(239,83,80,0.3)' }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#ef5350' }}>
                Revert Deposits
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                This will permanently delete all deposits on the selected date and roll back the
                deposited amounts on the linked egg sales. This cannot be undone.
              </Alert>
              <TextField
                label="Deposit Date to Revert"
                type="date"
                fullWidth
                value={reverseDepositDate}
                onChange={(e) => setReverseDepositDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(239,83,80,0.3)' }}>
              <Button
                onClick={() => setOpenReverseDepositDialog(false)}
                sx={{ color: 'rgba(255,255,255,0.7)' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReverseDeposits}
                variant="contained"
                disabled={reverseDepositsMutation.isPending}
                sx={{
                  background: 'linear-gradient(135deg, #ef5350 0%, #e57373 100%)',
                  color: '#ffffff',
                  '&:hover': { background: 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)' },
                }}
              >
                {reverseDepositsMutation.isPending ? (
                  <CircularProgress size={24} sx={{ color: '#fff' }} />
                ) : (
                  'Confirm Revert'
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Revert Deposit Confirm Dialog */}
          <Dialog
            open={!!revertConfirmSale}
            onClose={() => setRevertConfirmSale(null)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Revert Deposit</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                This will delete all deposit records for this sale ({revertConfirmSale?.total_price}
                ) and reset its status to pending. Are you sure?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRevertConfirmSale(null)}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={confirmRevertDeposit}
                disabled={reverseDepositsMutation.isPending}
              >
                {reverseDepositsMutation.isPending ? 'Reverting…' : 'Revert'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar Notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              variant="filled"
              sx={{
                width: '100%',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                '& .MuiAlert-icon': {
                  fontSize: '24px',
                },
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      )}
      {activeTab === 1 && <BrokenEggSalesPage />}
    </Box>
  );
};

export default EggSalesPage;
